// Single source of truth for role → route mapping and role helpers.
// Imported by the route guard (RoleRoute) and the role-based dashboard. Kept free
// of MUI / heavy imports so it can be pulled in anywhere cheaply.

export const ROLE_PRIORITY: string[] = [
  'Survey.Admin',
  'Survey.Agent',
  'Scheduler.Admin',
  'Campaign.Manager',
  'Drip.Manager',
  'Graduate.Admin',
  'Graduate.Viewer',
  'Survey.Creator',
  'Scheduler.Instructor',
  'Student',
  'Teachers',
  'Faculty',
  'Survey.Viewer',
];

// Login-time landing route for each role. Each target route's RoleRoute allow-list
// includes that same role, so redirecting a user to their home route can never
// bounce again (no redirect loop) — see getHomeRoute below.
export const ROLE_ROUTES: Record<string, string> = {
  'Survey.Admin':         '/Surveys',
  'Survey.Agent':         '/Surveys',
  'Survey.Creator':       '/Surveys/creator',
  'Campaign.Manager':     '/ECM',
  // Drip Campaign Manager lands directly on their own tab. /ECM/Dashboard/drip lists
  // 'Drip.Manager' in its allow-list, so this cannot bounce back through the guard.
  'Drip.Manager':         '/ECM/Dashboard/drip',
  'Graduate.Admin':       '/ECM/Dashboard/graduates',
  'Graduate.Viewer':      '/ECM/Dashboard/graduates',
  'Survey.Viewer':        '/Surveys/view',
  'Teachers':             '/Surveys/teacher',
  'Faculty':              '/Surveys/faculty',
  'Scheduler.Admin':      '/Scheduler',
  'Scheduler.Instructor': '/Scheduler/instructor',
  'Student':              '/Student',
};

/** All roles the user holds. Supports the legacy single `role` and the `roles[]` array. */
export function getUserRoles(user: any): string[] {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length) return user.roles;
  return user.role ? [user.role] : [];
}

/**
 * A Drip Campaign Manager without a broader ECM role. It runs drip campaigns and nothing else, so
 * under /ECM it may open only its own Campaign Dashboard tabs. ECMRoutesWrapper seals every /ECM
 * route with this (the standalone /ECM/new-campaign and /ECM/job/:id included) and ECMDashboard
 * picks its tabs with it; both import it from here so the two cannot drift apart.
 */
export function isDripOnlyUser(roles: string[]): boolean {
  return roles.includes('Drip.Manager') &&
    !roles.some((r) => ['Survey.Admin', 'Campaign.Manager', 'Graduate.Admin', 'Graduate.Viewer'].includes(r));
}

/** Verity Leads and Website Leads: not in Verity, sub-tabs of Drip Campaign (after its own two views). */
export const DRIP_LEADS_PATH = '/ECM/Dashboard/drip/leads';
export const DRIP_WEBSITE_PATH = '/ECM/Dashboard/drip/website';

/**
 * Their retired top-level tab addresses, kept for old links, bookmarks and the docs. Each goes to its
 * Drip Campaign sub-tab, for every role that may open it (the same roles as before). Compared case-blind,
 * as React Router matches the route; a trailing slash is allowed.
 */
const RETIRED_DRIP_PATHS: Record<string, string> = {
  '/ecm/dashboard/leads': DRIP_LEADS_PATH,
  '/ecm/dashboard/website': DRIP_WEBSITE_PATH,
};

/** The Drip Campaign sub-tab a retired Verity Leads or Website Leads address now lives at, or null. */
export function retiredDripPath(pathname: string): string | null {
  return RETIRED_DRIP_PATHS[pathname.replace(/\/+$/, '').toLowerCase()] ?? null;
}

/** The /ECM addresses a drip-only user may stay on: the four Drip Campaign views. */
const DRIP_ONLY_PATHS = /^\/ECM\/Dashboard\/drip(\/(layout-template|leads|website))?\/?$/;

/**
 * Where a drip-only user who opened `pathname` belongs instead, or null to stay. The retired Verity
 * Leads and Website Leads addresses go to their sub-tabs, and the retired Templates address to the drip
 * layout; everything else (All Campaigns, Campaign Portal and the retired SendGrid Templates address
 * under it, /ECM/new-campaign, /ECM/job/:id) to Drip Campaign. Each target is itself allowed above, so
 * this can never redirect twice.
 */
export function dripOnlyRedirect(pathname: string): string | null {
  if (DRIP_ONLY_PATHS.test(pathname)) return null;
  const moved = retiredDripPath(pathname);
  if (moved) return moved;
  return /^\/ECM\/Dashboard\/email-templates\/?$/.test(pathname)
    ? '/ECM/Dashboard/drip/layout-template'
    : '/ECM/Dashboard/drip';
}

/** Highest-priority role the user holds (or their single `role` as a fallback). */
export function getPrimaryRole(user: any): string | undefined {
  const roles = getUserRoles(user);
  return ROLE_PRIORITY.find(r => roles.includes(r)) ?? user?.role;
}

/**
 * The route this user should land on.
 * Returns a route the user is GUARANTEED to be allowed on (each ROLE_ROUTES target
 * lists that role in its allow-list), or '/SIMS' (the unguarded login page) when the
 * user has no recognised role — so a redirect here can never loop back into a guard.
 */
export function getHomeRoute(user: any): string {
  const primary = getPrimaryRole(user);
  if (primary && ROLE_ROUTES[primary]) return ROLE_ROUTES[primary];
  return '/SIMS';
}
